/*
  PLS calendar helper
  -------------------
  Shared by the show hero and the at-a-glance schedule, so "add this to my
  calendar" behaves identically wherever it appears on the Pendleton pages.

  Markup contract: any element carrying data-pls-cal is a trigger. The value is
  a JSON object describing one event:

    {
      "title":    "2026 Pendleton Leather Show",
      "start":    "2026-11-04T09:00:00-08:00",   // ISO 8601 WITH an offset
      "end":      "2026-11-07T17:00:00-08:00",
      "allDay":   false,                          // optional
      "location": "Pendleton Convention Center, 1601 Westgate, Pendleton, OR",
      "url":      "https://illumeconnect.com/pages/pendleton-leather-show",
      "details":  "Plain text. Newlines are fine."
    }

  Offsets matter. The show is in Pacific time and the people adding it to a
  calendar are not all in Pacific time, so every timestamp is converted to UTC
  before it is written out. A naive local-time string would land an Idaho
  attendee an hour off.
*/
(function () {
  'use strict';

  if (window.PLSCalendar) return;

  var FOLD_AT = 74;

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  /* ---- UTC stamp: 20261104T170000Z ---- */
  function stampUTC(date) {
    return (
      date.getUTCFullYear() +
      pad(date.getUTCMonth() + 1) +
      pad(date.getUTCDate()) +
      'T' +
      pad(date.getUTCHours()) +
      pad(date.getUTCMinutes()) +
      pad(date.getUTCSeconds()) +
      'Z'
    );
  }

  /* ---- Floating date stamp for all-day events: 20261104 ----
     An all-day event has no time, so it must NOT be converted to UTC. Doing so
     drags the event onto the previous day for anyone west of Greenwich. The
     date parts are read back in the original offset instead. */
  function stampDate(iso) {
    var m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? m[1] + m[2] + m[3] : null;
  }

  function addDays(iso, n) {
    var m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    var d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    d.setUTCDate(d.getUTCDate() + n);
    return d.getUTCFullYear() + pad(d.getUTCMonth() + 1) + pad(d.getUTCDate());
  }

  /* RFC 5545 escaping, then folding at 74 octets. Outlook is the strict one:
     an unfolded 200-character DESCRIPTION is silently dropped by some builds. */
  function escapeText(value) {
    return String(value == null ? '' : value)
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\r?\n/g, '\\n');
  }

  function fold(line) {
    if (line.length <= FOLD_AT) return line;
    var out = line.slice(0, FOLD_AT);
    var rest = line.slice(FOLD_AT);
    while (rest.length) {
      out += '\r\n ' + rest.slice(0, FOLD_AT - 1);
      rest = rest.slice(FOLD_AT - 1);
    }
    return out;
  }

  function uid(evt) {
    var seed = (evt.title || 'pls') + '|' + (evt.start || '');
    var hash = 0;
    for (var i = 0; i < seed.length; i++) {
      hash = (hash << 5) - hash + seed.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash).toString(36) + '-pls@illumeconnect.com';
  }

  function buildICS(evt) {
    var lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Illume Connect//Pendleton Leather Show//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      'UID:' + uid(evt),
      'DTSTAMP:' + stampUTC(new Date())
    ];

    if (evt.allDay) {
      // DTEND on an all-day VEVENT is exclusive, so a single day needs +1.
      lines.push('DTSTART;VALUE=DATE:' + stampDate(evt.start));
      lines.push('DTEND;VALUE=DATE:' + addDays(evt.end || evt.start, 1));
    } else {
      lines.push('DTSTART:' + stampUTC(new Date(evt.start)));
      lines.push('DTEND:' + stampUTC(new Date(evt.end || evt.start)));
    }

    lines.push(fold('SUMMARY:' + escapeText(evt.title)));
    if (evt.location) lines.push(fold('LOCATION:' + escapeText(evt.location)));

    var details = evt.details || '';
    if (evt.url) details = details ? details + '\n\n' + evt.url : evt.url;
    if (details) lines.push(fold('DESCRIPTION:' + escapeText(details)));
    if (evt.url) lines.push(fold('URL:' + escapeText(evt.url)));

    lines.push('END:VEVENT', 'END:VCALENDAR');
    return lines.join('\r\n');
  }

  function googleURL(evt) {
    var dates;
    if (evt.allDay) {
      dates = stampDate(evt.start) + '/' + addDays(evt.end || evt.start, 1);
    } else {
      dates = stampUTC(new Date(evt.start)) + '/' + stampUTC(new Date(evt.end || evt.start));
    }

    var details = evt.details || '';
    if (evt.url) details = details ? details + '\n\n' + evt.url : evt.url;

    var params = [
      'action=TEMPLATE',
      'text=' + encodeURIComponent(evt.title || ''),
      'dates=' + dates,
      'details=' + encodeURIComponent(details),
      'location=' + encodeURIComponent(evt.location || '')
    ];
    return 'https://calendar.google.com/calendar/render?' + params.join('&');
  }

  function slug(value) {
    return String(value || 'event')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
  }

  function download(evt) {
    var blob = new Blob([buildICS(evt)], { type: 'text/calendar;charset=utf-8' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = slug(evt.title) + '.ics';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoked late: Safari has been known to cancel an in-flight download when
    // the object URL disappears in the same tick as the click.
    setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
  }

  function parse(trigger) {
    try {
      return JSON.parse(trigger.getAttribute('data-pls-cal'));
    } catch (e) {
      return null;
    }
  }

  /* ---------- Menu ----------
     Two calendar worlds, one button. Google wants a URL, everything else
     (Apple, Outlook, Fantastical, Thunderbird) wants an .ics file. */
  function closeAll(except) {
    document.querySelectorAll('.pls-cal[data-open="true"]').forEach(function (el) {
      if (el === except) return;
      el.setAttribute('data-open', 'false');
      var btn = el.querySelector('[data-pls-cal]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-pls-cal]');

    if (trigger) {
      event.preventDefault();
      var wrap = trigger.closest('.pls-cal');
      if (!wrap) {
        var direct = parse(trigger);
        if (direct) download(direct);
        return;
      }
      var open = wrap.getAttribute('data-open') === 'true';
      closeAll(wrap);
      wrap.setAttribute('data-open', open ? 'false' : 'true');
      trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
      return;
    }

    var choice = event.target.closest('[data-pls-cal-choice]');
    if (choice) {
      var host = choice.closest('.pls-cal');
      var evt = host ? parse(host.querySelector('[data-pls-cal]')) : null;
      if (!evt) return;

      if (choice.getAttribute('data-pls-cal-choice') === 'google') {
        // Left as a real link so middle-click and "open in new tab" still work.
        choice.setAttribute('href', googleURL(evt));
      } else {
        event.preventDefault();
        download(evt);
      }
      if (host) host.setAttribute('data-open', 'false');
      return;
    }

    closeAll(null);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeAll(null);
  });

  window.PLSCalendar = {
    ics: buildICS,
    google: googleURL,
    download: download
  };
})();
